import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { RichMediaState } from '@bonusly/common-action-input';
import {
	Box,
	Button,
	DatePicker,
	FormControl,
	FormErrorMessage,
	FormLabel,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Stack,
	Tag,
	Text,
	Textarea,
	useColorModeValue,
	useToast,
	VStack,
	Wrap,
	XOutline,
} from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';
import { ProductTier, useCurrentCompany } from '@bonusly/who-am-i';

import { useFetchRecipients } from '../../../../components/recognition/give-box/hooks/use-fetch-recipients';
import { Goal, GoalStatus } from '../../../../types/goal';
import { useInitiatives } from '../../../admin-growth/hooks/use-initiatives';
import { useHashtags } from '../../../bonus-feed/features/recognition/api/hooks/hashtags';
import { useUpdateGoal } from '../../hooks/use-goals';
import { CollaboratorSelector } from './collaborator-selector';
import { GoalInitiativeCard } from './goal-initiative-card';
import { GoalStatusSelect } from './goal-status-select';

interface FormData {
	title: string;
	summary: string;
	status: GoalStatus;
	startDate: string;
	endDate: string;
	tags: string[];
	collaborators: Recipient[];
	initiativeId: string;
}

interface EditGoalModalProps {
	isOpen: boolean;
	onClose: () => void;
	goal: Goal | null;
	onSuccess?: (goal: any) => void;
}

export const EditGoalModal: FC<EditGoalModalProps> = ({ isOpen, onClose, goal, onSuccess }) => {
	const toast = useToast();
	const updateGoal = useUpdateGoal();
	const company = useCurrentCompany();
	const bgColor = useColorModeValue('white', 'gray.800');
	const borderColor = useColorModeValue('gray.200', 'gray.600');

	const isOrganizationTier = company.product_tier === ProductTier.TierThree;
	const { data: initiatives = [] } = useInitiatives(isOrganizationTier);

	// Common styles for consistent form field styling
	const labelContainerStyle = {
		justify: 'space-between' as const,
		align: 'center' as const,
		mb: 1,
	};

	const formLabelStyle = {
		fontSize: 'sm',
		fontWeight: 'medium',
		color: 'text.default',
		mb: 0,
	};

	const optionalTextStyle = {
		fontSize: 'sm',
		color: 'text.softest',
	};

	// Helper function to get initiative ID from different possible field names
	const getInitiativeId = (goalData: Goal | null) => {
		if (!goalData) return '';

		// Try different possible field names
		const initiativeId = goalData.initiative_id || (goalData as any).initiativeId || goalData.initiative?.id || '';

		return initiativeId;
	};

	// Helper function to transform UserCollaborator to Recipient format
	const transformCollaborators = (collaborators: any[]): Recipient[] => {
		if (!Array.isArray(collaborators)) return [];

		return collaborators.map((collaborator) => ({
			...collaborator,
			// Ensure imageSrc is set from the available image fields
			imageSrc: collaborator.imageSrc || collaborator.fullPicUrl || collaborator.profilePicUrl || '',
		}));
	};

	// Get company values aka hashtags
	const { data: hashtags } = useHashtags({
		params: {
			display_format: 'index',
		},
	});

	// Real search function using useFetchRecipients hook
	const fetchRecipients = useFetchRecipients({
		include_self: false,
		include_non_receivable: true,
		search_usergroups: false,
		search_users: true,
	});

	const searchCollaborators = async (query: string): Promise<Recipient[]> => {
		const emptyState = {
			mentions: [],
			recipients: [],
			hashtags: [],
			json: null,
			plainText: '',
			text: '',
		} as unknown as RichMediaState;

		const results = await fetchRecipients({
			query,
			state: emptyState,
		});

		return results;
	};

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<FormData>({
		defaultValues: {
			title: goal?.title || '',
			summary: goal?.summary || '',
			status: (goal?.status as GoalStatus) || 'on_track',
			startDate: goal?.startDate ? new Date(goal.startDate).toISOString() : '',
			endDate: goal?.endDate ? new Date(goal.endDate).toISOString() : '',
			tags: Array.isArray(goal?.tags) ? goal.tags : [],
			collaborators: transformCollaborators(goal?.collaborators || []),
			initiativeId: getInitiativeId(goal),
		},
	});

	// Update form when goal changes
	useEffect(() => {
		if (goal) {
			reset({
				title: goal.title || '',
				summary: goal.summary || '',
				status: (goal.status as GoalStatus) || 'on_track',
				startDate: goal.startDate ? new Date(goal.startDate).toISOString() : '',
				endDate: goal.endDate ? new Date(goal.endDate).toISOString() : '',
				tags: Array.isArray(goal.tags) ? goal.tags : [],
				collaborators: transformCollaborators(goal.collaborators || []),
				initiativeId: getInitiativeId(goal),
			});
		}
	}, [goal, reset]);

	const handleClose = () => {
		reset();
		onClose();
	};

	const onSubmit = async (data: FormData) => {
		if (!goal) {
			return;
		}

		try {
			// Transform collaborators (Recipient objects) to user IDs for the API
			const collaboratorIds = data.collaborators.map((collaborator) => collaborator.id);

			const updateData = {
				...data,
				collaborators: collaboratorIds,
			};

			await updateGoal.mutateAsync({ id: goal.id, data: updateData });
			toast({
				title: 'Goal updated successfully!',
				status: 'success',
				duration: 3000,
			});

			onSuccess?.(data);
			handleClose();
		} catch (error) {
			console.error('Failed to update goal:', error);
			toast({
				title: 'Failed to update goal',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		}
	};

	if (!goal) {
		return null;
	}

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
			<ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
			<ModalContent bg={bgColor} borderRadius="xl" border="1px solid" borderColor={borderColor} maxH="calc(100vh - 40px)" display="flex" flexDirection="column">
				<ModalHeader pb={3}>
					<Text fontSize="xl" fontWeight="semibold">
						Edit goal
					</Text>
				</ModalHeader>
				<ModalCloseButton />

				<ModalBody py={{ base: 4, md: 5 }} px={{ base: 4, md: 5 }} overflowY="auto" flex="1">
					<form id="edit-goal-form" onSubmit={handleSubmit(onSubmit)}>
						<VStack spacing={4} align="stretch">
							{/* Goal Title */}
							<FormControl isInvalid={!!errors.title}>
								<HStack {...labelContainerStyle}>
									<FormLabel {...formLabelStyle}>Goal title</FormLabel>
								</HStack>
								<Controller
									name="title"
									control={control}
									rules={{ required: 'Goal title is required' }}
									render={({ field }) => (
										<Input
											{...field}
											placeholder="Enter goal title"
											size="md"
											borderRadius="md"
											border="1px solid"
											borderColor="gray.300"
											_focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
										/>
									)}
								/>
								<FormErrorMessage>{errors.title?.message}</FormErrorMessage>
							</FormControl>

							{/* Goal Details */}
							<FormControl>
								<HStack {...labelContainerStyle}>
									<FormLabel {...formLabelStyle}>Goal details</FormLabel>
									<Text {...optionalTextStyle}>Optional</Text>
								</HStack>
								<Controller
									name="summary"
									control={control}
									render={({ field }) => (
										<Textarea
											{...field}
											placeholder="Add the details of what you're trying to achieve..."
											rows={4}
											borderRadius="md"
											border="1px solid"
											borderColor="gray.300"
											_focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
										/>
									)}
								/>
							</FormControl>

							{/* Current Status */}
							<FormControl>
								<HStack {...labelContainerStyle}>
									<FormLabel {...formLabelStyle}>Current status</FormLabel>
								</HStack>
								<Controller name="status" control={control} render={({ field }) => <GoalStatusSelect value={field.value} onChange={field.onChange} />} />
							</FormControl>

							{/* Dates */}
							<Stack spacing={4} align="stretch" direction={{ base: 'column', md: 'row' }}>
								<VStack spacing={0} align="stretch" flex={1}>
									<HStack {...labelContainerStyle}>
										<FormLabel {...formLabelStyle}>Start date</FormLabel>
										<Text {...optionalTextStyle}>Optional</Text>
									</HStack>
									<FormControl>
										<Controller
											name="startDate"
											control={control}
											render={({ field }) => (
												<DatePicker
													value={field.value ? new Date(field.value) : null}
													onChange={(date) => field.onChange(date?.toISOString())}
													displayFormat="long"
													isClearable={false}
													width="full"
												/>
											)}
										/>
									</FormControl>
								</VStack>

								<VStack spacing={0} align="stretch" flex={1}>
									<HStack {...labelContainerStyle}>
										<FormLabel {...formLabelStyle}>Target date</FormLabel>
										<Text {...optionalTextStyle}>Optional</Text>
									</HStack>
									<FormControl>
										<Controller
											name="endDate"
											control={control}
											render={({ field }) => (
												<DatePicker
													value={field.value ? new Date(field.value) : null}
													onChange={(date) => field.onChange(date?.toISOString())}
													displayFormat="long"
													isClearable={false}
													width="full"
												/>
											)}
										/>
									</FormControl>
								</VStack>
							</Stack>

							{/* Collaborators */}
							<FormControl>
								<HStack {...labelContainerStyle}>
									<FormLabel {...formLabelStyle}>Collaborators</FormLabel>
									<Text {...optionalTextStyle}>Optional</Text>
								</HStack>
								<Controller
									name="collaborators"
									control={control}
									render={({ field }) => (
										<Box px="1px">
											<CollaboratorSelector
												value={field.value}
												onSearch={searchCollaborators}
												onSelect={(collaborator: Recipient) => {
													if (!field.value.find((c: Recipient) => c.id === collaborator.id)) {
														field.onChange([...field.value, collaborator]);
													}
												}}
												onRemove={(collaborator: Recipient) => {
													field.onChange(field.value.filter((c: Recipient) => c.id !== collaborator.id));
												}}
												placeholder="Type to add a collaborator..."
											/>
										</Box>
									)}
								/>
							</FormControl>

							{/* Supporting Initiative - Only show for Organization tier */}
							{isOrganizationTier && (
								<FormControl>
									<HStack {...labelContainerStyle}>
										<FormLabel {...formLabelStyle}>Supporting initiative</FormLabel>
										<Text {...optionalTextStyle}>Optional</Text>
									</HStack>
									<Controller
										name="initiativeId"
										control={control}
										render={({ field }) => {
											const selectedInitiative = initiatives.find((initiative) => initiative.id === field.value);

											return (
												<VStack spacing={3} align="stretch">
													{!selectedInitiative && (
														<Box px="1px">
															<Select
																{...field}
																placeholder="Select an initiative..."
																borderRadius="md"
																border="1px solid"
																borderColor="gray.300"
																_focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
															>
																{initiatives.map((initiative) => (
																	<option key={initiative.id} value={initiative.id}>
																		{initiative.title}
																	</option>
																))}
															</Select>
														</Box>
													)}
													{selectedInitiative && (
														<GoalInitiativeCard initiative={selectedInitiative} showRemoveButton={true} onRemove={() => field.onChange('')} />
													)}
												</VStack>
											);
										}}
									/>
								</FormControl>
							)}

							{/* Company Values */}
							<FormControl>
								<HStack {...labelContainerStyle} mb={2}>
									<FormLabel {...formLabelStyle}>Which company values does this reflect?</FormLabel>
									<Text {...optionalTextStyle}>Optional</Text>
								</HStack>
								<Controller
									name="tags"
									control={control}
									render={({ field }) => (
										<Wrap>
											{hashtags?.data?.result?.map((hashtag: string) => {
												const isSelected = field.value.includes(hashtag);
												return (
													<Tag
														key={hashtag}
														variant={isSelected ? 'solid' : 'subtle'}
														size="sm"
														borderRadius="full"
														fontWeight="medium"
														width="fit-content"
														fontSize="sm"
														height={8}
														cursor="pointer"
														border="1px solid"
														borderColor="border.default"
														borderWidth="1px"
														py={1.5}
														px={3}
														color={isSelected ? 'text.onColor' : 'text.default'}
														bg={isSelected ? 'secondary.bg' : 'bg.surface'}
														onClick={() => {
															if (isSelected) {
																field.onChange(field.value.filter((h: string) => h !== hashtag));
															} else {
																field.onChange([...field.value, hashtag]);
															}
														}}
														_hover={{
															bg: isSelected ? '' : 'secondary.bgAlpha',
														}}
													>
														<HStack spacing={1} align="center">
															<Text size="sm">{hashtag}</Text>
															{isSelected && <XOutline size="xs" color="info.icon" />}
														</HStack>
													</Tag>
												);
											})}
										</Wrap>
									)}
								/>
								<HStack {...labelContainerStyle} mt={2}>
									<Text {...optionalTextStyle}>Click to select/deselect</Text>
								</HStack>
							</FormControl>
						</VStack>
					</form>
				</ModalBody>

				<ModalFooter py={3} px={{ base: 4, md: 5 }} bg={bgColor} borderTop="1px solid" borderColor={borderColor} borderBottomRadius="xl">
					<HStack spacing={3}>
						<Button variant="ghost" colorScheme="primary" onClick={handleClose}>
							Cancel
						</Button>
						<Button form="edit-goal-form" type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText="Updating...">
							Save goal
						</Button>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
